import { Fragment, useEffect, useMemo } from 'react'
import { datadogLogs } from '@datadog/browser-logs'

export type InlineSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; label: string; href: string }

const LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g

/**
 * Split `text` into literal-text and `[label](url)` link segments. Only link
 * syntax is interpreted; every other character (including bare `*`, `[`, `]`,
 * `#`, `$`) is emitted verbatim so footnote markers in legal copy are not
 * reinterpreted as markdown.
 */
export const parseInlineLinkSegments = (text: string): InlineSegment[] => {
  const segments: InlineSegment[] = []
  let lastIndex = 0
  for (const match of text.matchAll(LINK_PATTERN)) {
    const start = match.index ?? 0
    if (start > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, start) })
    }
    segments.push({ type: 'link', label: match[1], href: match[2] })
    lastIndex = start + match[0].length
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) })
  }
  return segments
}

/**
 * Allow app-relative paths (`/foo`, not `//host`) and absolute `http(s)` URLs.
 * Reject everything else (`javascript:`, `data:`, protocol-relative, etc.).
 */
export const isSafeLinkHref = (href: string): boolean => {
  if (href.startsWith('/')) return !href.startsWith('//')
  try {
    const { protocol } = new URL(href)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

const isExternalHref = (href: string): boolean =>
  href.startsWith('http://') || href.startsWith('https://')

export interface InlineLinkTextProps {
  text: string
  /** Class applied to rendered anchors. Defaults to `underline`. */
  className?: string
}

/**
 * Render a string with embedded `[label](url)` links as inline React nodes.
 * Safe links become anchors (external ones open in a new tab); unsafe links
 * degrade to their plain label text and emit a Datadog warning. Returns a
 * fragment so it nests inside existing text wrappers without adding block
 * elements.
 */
export const InlineLinkText = ({
  text,
  className = 'underline',
}: InlineLinkTextProps): JSX.Element => {
  const segments = useMemo(() => parseInlineLinkSegments(text), [text])

  useEffect(() => {
    segments.forEach((segment) => {
      if (segment.type === 'link' && !isSafeLinkHref(segment.href)) {
        datadogLogs.logger.warn('feature-announcement embedded link rejected', {
          href: segment.href,
        })
      }
    })
  }, [segments])

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          return <Fragment key={index}>{segment.value}</Fragment>
        }
        if (!isSafeLinkHref(segment.href)) {
          return <Fragment key={index}>{segment.label}</Fragment>
        }
        const externalProps = isExternalHref(segment.href)
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {}
        return (
          <a
            key={index}
            href={segment.href}
            className={className}
            {...externalProps}
          >
            {segment.label}
          </a>
        )
      })}
    </>
  )
}
